'use client';

import { useCallback } from 'react';
import { useTranslations } from 'next-intl';
import type {
  BodyScrollEvent,
  CellContextMenuEvent,
  ColDef,
  GetRowIdParams,
  GridApi,
  GridReadyEvent,
  RowClickedEvent,
  SortChangedEvent,
} from 'ag-grid-community';
import { Box, HStack } from '@terra/ui-styled-system';
import {
  MetaDefault,
  Spinner,
  TABLE_IDS,
  Table,
} from '@terra/ui-design-system';
import { WorkHistoryIcon } from '@terra/ui-icons';
import type { AgentExecutionDto } from '@terra/shared-contracts-agent-execution';
import type { AgentExecutionListSort } from '../../../store/tab/agentExecutionsTabScopedFilterStore';
import { EmptyState } from '../../EmptyState';
import {
  LIST_PAGE_TABLE_HEADER_CSS,
  LIST_PAGE_TABLE_HEADER_HEIGHT_PX,
  LIST_PAGE_TABLE_THEME_PARAMS,
} from '../../common/list-page-table-theme';
import { ListPageTableBodyLayer } from '../../common/ListPageTableBodyLayer';
import { useListPageTablePersistence } from '../../common/useListPageTablePersistence';
import { isRowClickDisabledTarget } from '../rowClickBlocker';
import { shouldLoadMoreExecutions } from '../executions-table/executions-list.model';
import { isAgentExecutionsSortableColumn } from './agent-executions-list.model';
import { useAgentExecutionsTableColumnDefs } from './useAgentExecutionsTableColumnDefs';

const ROW_HEIGHT_PX = 36;
const USER_SORT_SOURCE = 'uiColumnSorted';
const DEFAULT_COL_DEF: ColDef<AgentExecutionDto> = {
  sortable: false,
  filter: false,
  resizable: true,
  lockPinned: true,
  sortingOrder: ['desc', 'asc', null],
};
const ROW_STYLE = { cursor: 'pointer' };

type AgentExecutionsTableProps = {
  executions: AgentExecutionDto[];
  sort: AgentExecutionListSort;
  isLoading: boolean;
  isFetchingMore: boolean;
  hasMore: boolean;
  hasActiveFilters: boolean;
  onLoadMore: () => void;
  onSortChange: (sort: AgentExecutionListSort) => void;
  onOpenExecution: (execution: AgentExecutionDto) => void;
  onOpenExecutionContextMenu: ({
    event,
    execution,
  }: {
    event: MouseEvent;
    execution: AgentExecutionDto;
  }) => void;
};

const getRowId = (params: GetRowIdParams<AgentExecutionDto>) => params.data.id;

function AgentExecutionsTableEmptyState({
  hasActiveFilters,
}: Pick<AgentExecutionsTableProps, 'hasActiveFilters'>) {
  const tTable = useTranslations('proxy.executions.table');
  const tAgent = useTranslations('agentExecutions.table');

  return (
    <EmptyState
      icon={WorkHistoryIcon}
      title={
        hasActiveFilters ? tTable('emptyFilteredTitle') : tTable('emptyTitle')
      }
      description={
        hasActiveFilters
          ? tTable('emptyFilteredDescription')
          : tAgent('emptyDescription')
      }
    />
  );
}

export function AgentExecutionsTable({
  executions,
  sort,
  isLoading,
  isFetchingMore,
  hasMore,
  hasActiveFilters,
  onLoadMore,
  onSortChange,
  onOpenExecution,
  onOpenExecutionContextMenu,
}: AgentExecutionsTableProps) {
  const t = useTranslations('proxy.executions.table');
  const columnDefs = useAgentExecutionsTableColumnDefs();
  const persistence = useListPageTablePersistence(
    TABLE_IDS.PROXY_AGENT_EXECUTIONS_LIST,
  );
  const loadMoreIfNearEnd = useCallback(
    (api: GridApi<AgentExecutionDto>) => {
      if (
        shouldLoadMoreExecutions({
          lastDisplayedRowIndex: api.getLastDisplayedRowIndex(),
          loadedCount: executions.length,
          hasMore,
          isFetchingMore,
        })
      ) {
        onLoadMore();
      }
    },
    [executions.length, hasMore, isFetchingMore, onLoadMore],
  );
  const handleGridReady = useCallback(
    (event: GridReadyEvent<AgentExecutionDto>) => {
      event.api.applyColumnState({
        state: sort ? [{ colId: sort.colId, sort: sort.sort }] : [],
        defaultState: { sort: null },
      });
    },
    [sort],
  );
  const handleSortChanged = useCallback(
    (event: SortChangedEvent<AgentExecutionDto>) => {
      if (event.source !== USER_SORT_SOURCE) {
        return;
      }

      const sortedColumn = event.api
        .getColumnState()
        .find((column) => column.sort === 'asc' || column.sort === 'desc');

      onSortChange(
        sortedColumn && isAgentExecutionsSortableColumn(sortedColumn.colId)
          ? {
              colId: sortedColumn.colId,
              sort: sortedColumn.sort === 'asc' ? 'asc' : 'desc',
            }
          : null,
      );
    },
    [onSortChange],
  );
  const handleRowClicked = useCallback(
    (event: RowClickedEvent<AgentExecutionDto>) => {
      if (!event.data || isRowClickDisabledTarget(event.event)) {
        return;
      }

      onOpenExecution(event.data);
    },
    [onOpenExecution],
  );
  const handleCellContextMenu = useCallback(
    (event: CellContextMenuEvent<AgentExecutionDto>) => {
      if (!event.data || !(event.event instanceof MouseEvent)) {
        return;
      }

      onOpenExecutionContextMenu({ event: event.event, execution: event.data });
    },
    [onOpenExecutionContextMenu],
  );

  return (
    <Box
      flex={1}
      w="full"
      h="full"
      overflow="hidden"
      position="relative"
      css={LIST_PAGE_TABLE_HEADER_CSS}
    >
      <Table
        columnDefs={columnDefs}
        persistence={persistence}
        rowData={executions}
        getRowId={getRowId}
        rowHeight={ROW_HEIGHT_PX}
        headerHeight={LIST_PAGE_TABLE_HEADER_HEIGHT_PX}
        noBorder={true}
        fontSize={12}
        headerFontSize={12}
        themeParams={LIST_PAGE_TABLE_THEME_PARAMS}
        animateRows={false}
        defaultColDef={DEFAULT_COL_DEF}
        suppressCellFocus={true}
        suppressMultiSort={true}
        hideColumnControls={true}
        fitColumnsOnGridSizeChanged={false}
        loading={isLoading}
        onGridReady={handleGridReady}
        onSortChanged={handleSortChanged}
        onBodyScroll={(event: BodyScrollEvent<AgentExecutionDto>) =>
          loadMoreIfNearEnd(event.api)
        }
        onRowDataUpdated={(event) => loadMoreIfNearEnd(event.api)}
        onRowClicked={handleRowClicked}
        onCellContextMenu={handleCellContextMenu}
        preventDefaultOnContextMenu
        getRowStyle={() => ROW_STYLE}
        suppressNoRowsOverlay
      />
      {executions.length === 0 && !isLoading ? (
        <ListPageTableBodyLayer>
          <AgentExecutionsTableEmptyState hasActiveFilters={hasActiveFilters} />
        </ListPageTableBodyLayer>
      ) : null}
      {isFetchingMore ? (
        <HStack
          position="absolute"
          bottom={3}
          left="50%"
          transform="translateX(-50%)"
          gap={2}
          px={3}
          py={1.5}
          borderRadius="full"
          bg="bg.default"
          boxShadow="sm"
          pointerEvents="none"
        >
          <Spinner size="sm" />
          <MetaDefault color="fg.muted">{t('loadingMore')}</MetaDefault>
        </HStack>
      ) : null}
    </Box>
  );
}
